import React, {useMemo} from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import {ChevronDown, ChevronUp, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight} from "lucide-react";
import toastr from "toastr";

// open the real PA portal via the sso handoff (see docs/sso-handoff); lands on the
// policy record, or the employee record when employeeId is given
const openPaView = async (policyId: string, employeeId: string | null = null) => {
    // open the tab synchronously (popup blockers allow it on click), then
    // mint an sso ticket and point the tab at the PA portal handoff
    const tab = window.open("", "_blank");
    try {
        const response = await fetch("/api/sso/paview", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({policyId, employeeId}),
        });
        if (!response.ok) {
            throw new Error("mint failed: " + response.status);
        }
        const res = await response.json();
        if (tab) {
            tab.location.href = res.url;
        } else {
            window.location.href = res.url;
        }
    } catch (e) {
        if (tab) tab.close();
        toastr.error("Could not open PA View");
    }
};

const openEmployeeView = async (employeeId: string, claimId?: string) => {
    const tab = window.open("", "_blank");
    try {
        const response = await fetch("/api/sso/employeeview", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({employeeId, claimId}),
        });
        if (!response.ok) {
            throw new Error("mint failed: " + response.status);
        }
        const res = await response.json();
        if (tab) {
            tab.location.href = res.url;
        } else {
            window.location.href = res.url;
        }
    } catch (e) {
        if (tab) tab.close();
        toastr.error("Could not open the employee portal");
    }
};

export const PolicyTable = ({ searchResult, onPolicySelect }) => {
    const data = useMemo(() => searchResult || [], [searchResult]);
    const columns = useMemo(
        () => [
            {
                Header: "",
                id: "id",
                Cell: ({ row }) => row.index + 1,
                style: {
                    fontWeight: "bold",
                }
            },
            {
                Header: "Name",
                accessor: (row) => `${row.name}`,
                Cell: ({ row }) => {
                    return (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => onPolicySelect(row.original)}>
                            {`${row.original.name}`}
                        </button>
                    );
                },
            },
            {
                Header: "Status",
                accessor: (row) => (row.isActive ? "Active" : "Inactive"),
                Cell: ({ value, row }) => (
                    <td className={value === "Active" ? "green" : "red"}>
                        {value}
                        {row.original.suspendClaims && (
                            <div style={{ display: "inline-block", marginTop: "2px", color: "#856404", backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "4px", padding: "2px 8px", fontSize: "12px", fontWeight: 700 }}>
                                Policy Claims Temporarily Paused
                            </div>
                        )}
                    </td>
                ),
            },
            {
                Header: "EA",
                accessor: (row) => (row.isEA === "Yes"? "Yes" : "No"),
                Cell: ({ value }) => (
                    <td className={value === "Yes" ? "red" : "green" }>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Scheduled",
                accessor: (row) => (row.accrualFrequency != null ? "Yes " + "(" + row.accrualFrequency + ")": "No"),
                style: {
                    textAlign: "center",
                },
                Cell: ({ value }) => (
                    <td className={value === "No" ? "green" : "red" }>
                        {value}
                    </td>
                )
            },
            {
                Header: "Language",
                accessor: (row) => (row.language === 'ENG' ? "English" : "French"),
                style: {
                    textAlign: "center",
                },
                Cell: ({ value }) => (
                    <td className={value === "English" ? "green" : "red"}>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Employees",
                accessor: "empCount",
                style: {
                    width: "30px",
                    textAlign: "right",
                }
            },
            {
                Header: "",
                id: "buttons",
                accessor: "actions",
                Cell: ({ row }) => (
                    <div className="text-center">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm search-action-btn"
                            onClick={() => {
                                window.open(
                                    "/ops/enroll.policy.record?policy_id=" + row.original.id,
                                    "_blank"
                                );
                            }}
                        >
                            OPS View
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm ms-3 search-action-btn"
                            onClick={() => openPaView(row.original.id)}
                        >
                            PA View
                        </button>
                    </div>
                ),
            },
        ], []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
        },
        useSortBy,
        usePagination
    );

    return (
        <>
            <div className="pt-2">
                Found <b>{data.length}</b>
            </div>
            <div className="dashboard-table-container">
                <table {...getTableProps()} className="table table-striped table-hover search-results-table">
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted ? (column.isSortedDesc ? <ChevronDown size={14}/> : <ChevronUp size={14}/>) : ''}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()} style={cell.column.style}>{cell.render("Cell")}</td>;
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="search-pagination">
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsLeft size={14}/>
                    </button>
                    <button onClick={previousPage} disabled={!canPreviousPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronLeft size={14}/>
                    </button>
                    <button onClick={nextPage} disabled={!canNextPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronRight size={14}/>
                    </button>
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsRight size={14}/>
                    </button>
                </div>
                <div>
                    <span>
                        Page{' '}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <span>
                        | Go to page:{' '}
                        <input
                            type="number"
                            className="form-control form-control-sm d-inline-block"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(pageNumber);
                            }}
                            style={{width: '50px', marginLeft: '5px'}}
                        />
                    </span>
                </div>
                <div>
                    <select
                        className="form-select form-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40].map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );
}

export const EmployeeTable = ({ searchResult, onEmployeeSelect, pageIndex, pageSize, totalRecords, onPageChange }) => {
    const data = useMemo(() => searchResult || [], [searchResult]);
    const pageCount = Math.ceil(totalRecords / pageSize);
    var rowStart = pageIndex * pageSize;
    const columns = useMemo(
        () => [
            {
                Header: "",
                id: "id",
                Cell: ({ row }) => rowStart + row.index + 1,
                style: {
                    fontWeight: "bold",
                }
            },
            {
                Header: "Employee Name",
                accessor: (row) => `${row.lastName}, ${row.firstName}`,
                Cell: ({ row }) => {
                    return (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => onEmployeeSelect(row.original)}>
                            {`${row.original.lastName}, ${row.original.firstName}`}
                        </button>
                    );
                },
            },
            {
                Header: "Status",
                accessor: "isActive",
                Cell: ({ value, row }) => (
                    <span className={value ? "text-success" : "text-danger"}>
                        {value ? "Active" : "Inactive"}
                        {row.original.suspendClaims && (
                            <div style={{ display: "inline-block", marginTop: "2px", color: "#856404", backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "4px", padding: "2px 8px", fontSize: "12px", fontWeight: 700 }}>
                                Policy Claims Temporarily Paused
                            </div>
                        )}
                    </span>
                ),
            },
            {
                Header: "PA",
                accessor: "isPlanAdmin",
                Cell: ({ value, row }) => (
                    <span>
                        {value ? "Yes" : "No"}
                        {value && row.original.planAdminInactive && <span style={{ marginLeft: "4px", fontSize: "11px", color: "#856404" }}>(inactive)</span>}
                    </span>
                ),
            },
            {
                Header: "Policy Name",
                accessor: "policyName",
                style: {
                    width: '300px',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word'
                },
                Cell: ({ row }) => (
                    <span>
                        {row.original.policyName}
                        {row.original.readOnly && <span style={{ marginLeft: "4px", fontSize: "11px", color: "#856404" }}>(read-only)</span>}
                    </span>
                ),
            },
            {
                Header: "EA",
                accessor: (row) => (row.isEA === "Yes"? "Yes" : "No"),
                Cell: ({ value }) => (
                    <td className={value === "Yes" ? "red" : "green" }>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Scheduled",
                accessor: (row) => (row.accrualFrequency != null ? `Yes (${row.accrualFrequency})`: "No"),
                Cell: ({ value }) => (

                    <td className={value === "No" ? "green" : "red" }>
                        {value}
                    </td>
                )
            },
            {
                Header: "Language",
                accessor: (row) => (row.language === 'ENG' ? "ENG" : "FR"),
                style: {
                    textAlign: "center",
                },
                Cell: ({ value }) => (
                    <td className={value === "ENG" ? "green" : "red"}>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Latest Claim",
                accessor: "latestClaim",
                Cell: ({ row }) => {
                    const { id, latestClaim } = row.original;
                    return latestClaim ? (
                        <button
                            className="btn-link btn-sm"
                            onClick={() => openEmployeeView(id, latestClaim.id)}
                        >
                            {latestClaim.claimNo}
                        </button>
                    ) : null;
                },
            },
            {
                Header: "Created At",
                accessor: "createdAt",
                Cell: ({ value }) => {
                    if (!value) return "N/A";
                    return new Date(value).toLocaleString("en-US", {
                        timeZone: "America/Denver",
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                    });
                }
            },
            {
                Header: "Created By",
                accessor: "createdBy",
                Cell: ({ row }) => row.original.createdBy
                    ? `${row.original.createdBy.firstName} ${row.original.createdBy.lastName}`
                    : ""
            },
            {
                Header: "",
                accessor: "buttons",
                id: "actions",
                width: "20%",
              Cell: ({ row }) => (
                <div className="text-center d-flex flex-column align-items-center gap-2" style={{ minWidth: '280px' }}>

                    <div className="d-flex flex-wrap justify-content-center gap-2">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm search-action-btn"
                            onClick={() => {
                                window.open(
                                    "/ops/enroll.policy.record?policy_id=" + row.original.polId,
                                    "_blank"
                                );
                            }}
                        >
                            Policy OPS
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm search-action-btn"
                            onClick={() => {
                                window.open(
                                    "/ops/enroll.employee.record?employee_id=" + row.original.id,
                                    "_blank"
                                );
                            }}
                        >
                            EE Ops
                        </button>
                    </div>

                    <div className="d-flex flex-wrap justify-content-center gap-2">
                        <button
                            type="button"
                            className="btn btn-dark btn-sm search-action-btn"
                            onClick={() => openEmployeeView(row.original.id)}
                        >
                            EE Portal
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark btn-sm search-action-btn"
                            onClick={() => openPaView(row.original.polId)}
                        >
                            PA View
                        </button>
                    </div>
                </div>
            ),
        }
        ],
        [pageIndex, pageSize]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        setPageSize,
        state,
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex, pageSize },
            manualPagination: true,
            pageCount,
        },
        useSortBy,
        usePagination
    );

    return (
        <>
            <div className="pt-2">
                Found <b>{totalRecords}</b> result{totalRecords !== 1 ? "s" : ""}
            </div>
            <div className="dashboard-table-container">
                <table {...getTableProps()} className="table table-striped table-hover search-results-table">
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())} style={column.style}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted ? (column.isSortedDesc ? <ChevronDown size={14}/> : <ChevronUp size={14}/>) : ''}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="search-pagination">
                <div>
                    <button onClick={() => onPageChange(0, pageSize)} disabled={!canPreviousPage} className="btn btn-outline-secondary btn-sm search-pagination-btn"><ChevronsLeft size={14}/></button>
                    <button onClick={() => onPageChange(pageIndex - 1, pageSize)} disabled={!canPreviousPage} className="btn btn-outline-secondary btn-sm search-pagination-btn"><ChevronLeft size={14}/></button>
                    <button onClick={() => onPageChange(pageIndex + 1, pageSize)} disabled={!canNextPage} className="btn btn-outline-secondary btn-sm search-pagination-btn"><ChevronRight size={14}/></button>
                    <button onClick={() => onPageChange(pageCount - 1, pageSize)} disabled={!canNextPage} className="btn btn-outline-secondary btn-sm search-pagination-btn"><ChevronsRight size={14}/></button>
                </div>
                <div>
                    <span>
                        Page{' '}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <span>
                        | Go to page:{' '}
                       <input
                        type="number"
                        className="form-control form-control-sm d-inline-block"
                        defaultValue={pageIndex + 1}
                        onChange={(e) => {
                            const p = e.target.value ? Number(e.target.value) - 1 : 0;
                            onPageChange(p, pageSize);
                        }}
                        style={{width: '50px', marginLeft: '5px'}}
                        />
                    </span>
                </div>
                <div>
                    <select
                    className="form-select form-select-sm"
                    value={pageSize}
                    onChange={(e) => {
                        onPageChange(pageIndex, Number(e.target.value));
                    }}
                    >
                    {[10, 20, 30, 40].map((size) => (
                        <option key={size} value={size}>
                        Show {size}
                        </option>
                    ))}
                    </select>
                </div>
            </div>
        </>
    );
}

export const EmployeeNotPaginatedTable = ({ searchResult, onEmployeeSelect }) => {
    const data = useMemo(() => searchResult || [], [searchResult]);

    const columns = useMemo(() => [
        {
            Header: "#",
            id: "id",
            Cell: ({ row }) => row.index + 1,
            style: { fontWeight: "bold" },
        },
        {
            Header: "Employee Name",
            accessor: (row) => `${row.lastName}, ${row.firstName}`,
            Cell: ({ row }) => (
                <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => onEmployeeSelect(row.original)}
                >
                    {`${row.original.lastName}, ${row.original.firstName}`}
                </button>
            ),
        },
        {
            Header: "Status",
            accessor: "isActive",
            Cell: ({ value }) => (
                <span className={value ? "text-success" : "text-danger"}>
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            Header: "PA",
            accessor: "isPlanAdmin",
            Cell: ({ value, row }) => (
                <span>
                    {value ? "Yes" : "No"}
                    {value && row.original.planAdminInactive && <span style={{ marginLeft: "4px", fontSize: "11px", color: "#856404" }}>(inactive)</span>}
                </span>
            ),
        },
        {
            Header: "Policy Name",
            accessor: "policyName",
            style: {
                width: '300px',
                whiteSpace: 'normal',
                wordWrap: 'break-word'
            },
            Cell: ({ row }) => (
                <span>
                    {row.original.policyName}
                    {row.original.readOnly && <span style={{ marginLeft: "4px", fontSize: "11px", color: "#856404" }}>(read-only)</span>}
                </span>
            ),
        },
        {
            Header: "EA",
            accessor: (row) => (row.isEA === "Yes" ? "Yes" : "No"),
            Cell: ({ value }) => (
                <td className={value === "Yes" ? "red" : "green"}>
                    {value}
                </td>
            ),
        },
        {
            Header: "Scheduled",
            accessor: (row) => (row.accrualFrequency != null ? `Yes (${row.accrualFrequency})`: "No"),
            Cell: ({ value }) => (

                <td className={value === "No" ? "green" : "red" }>
                    {value}
                </td>
            )
        },
        {
            Header: "Latest Claim",
            accessor: "latestClaim",
            Cell: ({ row }) => {
                const { id, latestClaim } = row.original;
                return latestClaim ? (
                    <button
                        className="btn-link btn-sm"
                        onClick={() => openEmployeeView(id, latestClaim.id)}
                    >
                        {latestClaim.claimNo}
                    </button>
                ) : null;
            },
        },
        {
            Header: "Created At",
            accessor: "createdAt",
            Cell: ({ value }) => {
                if (!value) return "N/A";
                return new Date(value).toLocaleString("en-US", {
                    timeZone: "America/Denver",
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                });
            },
        },
        {
            Header: "Created By",
            accessor: "createdBy",
            Cell: ({ row }) =>
                row.original.createdBy
                    ? `${row.original.createdBy.firstName} ${row.original.createdBy.lastName}`
                    : "",
        },
        {
            Header: "",
            accessor: "buttons",
            id: "actions",
            width: "20%",
            Cell: ({ row }) => (
                <div className="text-center">
                    <button
                        type="button"
                        className="btn btn-primary btn-sm search-action-btn"
                        onClick={() =>
                            window.open(`/ops/enroll.policy.record?policy_id=${row.original.polId}`, "_blank")
                        }
                    >
                        Policy OPS
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm ms-3 search-action-btn"
                        onClick={() =>
                            window.open(`/ops/enroll.employee.record?employee_id=${row.original.id}`, "_blank")
                        }
                    >
                        EE Ops
                    </button>
                    <button
                        type="button"
                        className="btn btn-dark btn-sm ms-3 search-action-btn"
                        onClick={() => openEmployeeView(row.original.id)}
                    >
                        EE Portal
                    </button>
                    <button
                        type="button"
                        className="btn btn-dark btn-sm ms-3 search-action-btn"
                        onClick={() => openPaView(row.original.polId)}
                    >
                        PA View
                    </button>
                </div>
            ),
        }
    ], [onEmployeeSelect]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows
    } = useTable(
        {
            columns,
            data,
        },
        useSortBy
    );

    return (
        <>
            <div className="pt-2">
                Found <b>{data.length}</b> result{data.length !== 1 ? "s" : ""}
            </div>
            <div className="dashboard-table-container">
                <table {...getTableProps()} className="table table-striped table-hover search-results-table">
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())} style={column.style}>
                                        {column.render("Header")}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export const ClaimTable = ({ searchResult, onEmployeeSelect }) => {
    const data = useMemo(() => searchResult || [], [searchResult]);
    const columns = useMemo(
        () => [
            {
                Header: "",
                id: "id",
                Cell: ({ row }) => row.index + 1,
                style: {
                    fontWeight: "bold",
                }
            },
            {
                Header: "Claim",
                accessor: (row) => `${row.claimNo}`,
                Cell: ({ row }) => {
                    return (
                        <button
                            onClick={() => openEmployeeView(row.original.id, row.original.claimId)}
                            type="button"
                            className="btn-link btn-sm"
                        >
                            {row.original.claimNo}
                        </button>
                    );
                },
            },
            {
                Header: "Employee Name",
                accessor: (row) => `${row.lastName}, ${row.firstName}`,
                Cell: ({ row }) => {
                    return (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => onEmployeeSelect(row.original)}>
                            {`${row.original.lastName}, ${row.original.firstName}`}
                        </button>
                    );
                },
            },
            {
                Header: "Status",
                accessor: (row) => (row.isActive ? "Active" : "Inactive"),
                Cell: ({ value }) => (
                    <td className={value === "Active" ? "green" : "red"}>
                        {value}
                    </td>
                ),
            },
            {
                Header: "EA",
                accessor: (row) => (row.isEA === "Yes"? "Yes" : "No"),
                Cell: ({ value }) => (
                    <td className={value === "Yes" ? "red" : "green" }>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Scheduled",
                accessor: (row) => (row.accrualFrequency != null ? `Yes (${row.accrualFrequency})`: "No"),
                Cell: ({ value }) => (

                    <td className={value === "No" ? "green" : "red" }>
                        {value}
                    </td>
                )
            },
            {
                Header: "Date of Birth",
                accessor: "dob",
                Cell: ({ value }) => {
                    const date = new Date(value);
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    });
                    return <span>{formattedDate}</span>;
                }
            },
            {
                Header: "Policy",
                accessor: "policyName"
            },
            {
                Header: "",
                id: "buttons",
                accessor: "actions",
                Cell: ({ row }) => (
                    <div className="text-center">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm search-action-btn"
                            onClick={() => {
                                window.open(
                                    "/ops/enroll.policy.record?policy_id=" + row.original.polId,
                                    "_blank"
                                );
                            }}
                        >
                            OPS View
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm ms-3 search-action-btn"
                            onClick={() => openPaView(row.original.polId)}
                        >
                            PA View
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark btn-sm ms-3 search-action-btn"
                            onClick={() => openEmployeeView(row.original.id, row.original.claimId)}
                        >
                            View EOB
                        </button>
                    </div>
                ),
            },
        ], []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        setPageSize,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
        },
        useSortBy,
        usePagination
    );

    return (
        <>
            <div className="pt-2">
                Found <b>{data.length}</b>
            </div>
            <div className="dashboard-table-container">
                <table {...getTableProps()} className="table table-striped table-hover search-results-table">
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted ? (column.isSortedDesc ? <ChevronDown size={14}/> : <ChevronUp size={14}/>) : ''}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="search-pagination">
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsLeft size={14}/>
                    </button>
                    <button onClick={previousPage} disabled={!canPreviousPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronLeft size={14}/>
                    </button>
                    <button onClick={nextPage} disabled={!canNextPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronRight size={14}/>
                    </button>
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsRight size={14}/>
                    </button>
                </div>
                <div>
                    <span>
                        Page{' '}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <span>
                        | Go to page:{' '}
                        <input
                            type="number"
                            className="form-control form-control-sm d-inline-block"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(pageNumber);
                            }}
                            style={{width: '50px', marginLeft: '5px'}}
                        />
                    </span>
                </div>
                <div>
                    <select
                        className="form-select form-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40].map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );
}

export const DependentTable = ({ searchResult, onEmployeeSelect }) => {
    const data = useMemo(() => searchResult || [], [searchResult]);
    const columns = useMemo(
        () => [
            {
                Header: "",
                id: "id",
                Cell: ({ row }) => row.index + 1,
                style: {
                    fontWeight: "bold",
                }
            },
            {
                Header: "Employee Name",
                accessor: (row) => `${row.lastName}, ${row.firstName}`,
                Cell: ({ row }) => {
                    return (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => onEmployeeSelect(row.original)}>
                            {`${row.original.lastName}, ${row.original.firstName}`}
                        </button>
                    );
                },
            },
            {
                Header: "Status",
                accessor: (row) => (row.isActive ? "Active" : "Inactive"),
                Cell: ({ value }) => (
                    <td className={value === "Active" ? "green" : "red"}>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Date of Birth",
                accessor: "dob",
                Cell: ({ value }) => {
                    const date = new Date(value);
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    });
                    return <span>{formattedDate}</span>;
                }
            },
            {
                Header: "Policy",
                accessor: "policyName"
            },
            {
                Header: "Latest Claim",
                accessor: "latestClaim",
                Cell: ({ row }) => {
                    const { id, latestClaim } = row.original;
                    return latestClaim ? (
                        <button
                            className="btn-link btn-sm"
                            onClick={() => openEmployeeView(id, latestClaim.id)}
                        >
                            {latestClaim.claimNo}
                        </button>
                    ) : null;
                },
            },
            {
                Header: "",
                accessor: "buttons",
                id: "actions",
                Cell: ({ row }) => (
                    <div className="text-center">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm search-action-btn"
                            onClick={() => {
                                window.open(
                                    "/ops/enroll.policy.record?policy_id=" + row.original.polId,
                                    "_blank"
                                );
                            }}
                        >
                            OPS View
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm ms-3 search-action-btn"
                            onClick={() => openPaView(row.original.polId, row.original.id)}
                        >
                            PA View
                        </button>
                        <button
                            type="button"
                            className="btn btn-dark btn-sm ms-3 search-action-btn"
                            onClick={() => openEmployeeView(row.original.id)}
                        >
                            EE Portal
                        </button>
                    </div>
                ),
            }
        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        setPageSize,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
        },
        useSortBy,
        usePagination
    );

    return (
        <>
            <div className="pt-2">
                Found <b>{data.length}</b>
            </div>
            <div className="dashboard-table-container">
                <table {...getTableProps()} className="table table-striped table-hover search-results-table">
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted ? (column.isSortedDesc ? <ChevronDown size={14}/> : <ChevronUp size={14}/>) : ''}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="search-pagination">
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsLeft size={14}/>
                    </button>
                    <button onClick={previousPage} disabled={!canPreviousPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronLeft size={14}/>
                    </button>
                    <button onClick={nextPage} disabled={!canNextPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronRight size={14}/>
                    </button>
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsRight size={14}/>
                    </button>
                </div>
                <div>
                    <span>
                        Page{' '}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <span>
                        | Go to page:{' '}
                        <input
                            type="number"
                            className="form-control form-control-sm d-inline-block"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(pageNumber);
                            }}
                            style={{width: '50px', marginLeft: '5px'}}
                        />
                    </span>
                </div>
                <div>
                    <select
                        className="form-select form-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40].map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );
}

export const UserTable = ({ searchResult, onUserSelect }) => {
    const data = useMemo(() => searchResult || [], [searchResult]);
    const columns = useMemo(
        () => [
            {
                Header: "",
                id: "id",
                Cell: ({ row }) => row.index + 1,
            },
            {
                Header: "Username",
                accessor: (row) => `${row.username}`,
                Cell: ({ row }) => {
                    return (
                        <button type="button" className="btn btn-link btn-sm" onClick={() => onUserSelect(row.original)}>
                            {`${row.original.username}`}
                        </button>
                    );
                },
            },
            {
                Header: "Name",
                accessor: (row) => `${row.lastName}, ${row.firstName}`,
                Cell: ({row}) => (
                    <span>
                        {`${row.original.lastName}, ${row.original.firstName}`}
                    </span>
                )
            },
            {
                Header: "Status",
                accessor: (row) => (row.isActive ? "Active" : "Inactive"),
                Cell: ({ value }) => (
                    <td className={value === "Active" ? "green" : "red"}>
                        {value}
                    </td>
                ),
            },
            {
                Header: "EA",
                accessor: (row) => (row.isEA === "Yes"? "Yes" : "No"),
                Cell: ({ value }) => (
                    <td className={value === "Yes" ? "red" : "green" }>
                        {value}
                    </td>
                ),
            },
            {
                Header: "Scheduled",
                accessor: (row) => (row.accrualFrequency != null ? `Yes (${row.accrualFrequency})`: "No"),
                Cell: ({ value }) => (

                    <td className={value === "No" ? "green" : "red" }>
                        {value}
                    </td>
                )
            },
        ], []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        gotoPage,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        pageOptions,
        setPageSize,
        state: {pageIndex, pageSize},
    } = useTable(
        {
            columns,
            data,
        },
        useSortBy,
        usePagination
    );

    return (
        <>
            <div className="pt-2">
                Found <b>{data.length}</b>
            </div>
            <div className="dashboard-table-container">
                <table {...getTableProps()} className="table table-striped table-hover search-results-table">
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render('Header')}
                                    <span>
                                        {column.isSorted ? (column.isSortedDesc ? <ChevronDown size={14}/> : <ChevronUp size={14}/>) : ''}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <div className="search-pagination">
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsLeft size={14}/>
                    </button>
                    <button onClick={previousPage} disabled={!canPreviousPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronLeft size={14}/>
                    </button>
                    <button onClick={nextPage} disabled={!canNextPage} className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronRight size={14}/>
                    </button>
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}
                            className="btn btn-outline-secondary btn-sm search-pagination-btn">
                        <ChevronsRight size={14}/>
                    </button>
                </div>
                <div>
                    <span>
                        Page{' '}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{' '}
                    </span>
                    <span>
                        | Go to page:{' '}
                        <input
                            type="number"
                            className="form-control form-control-sm d-inline-block"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(pageNumber);
                            }}
                            style={{width: '50px', marginLeft: '5px'}}
                        />
                    </span>
                </div>
                <div>
                    <select
                        className="form-select form-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40].map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );
}
